import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Sparkles,
  Loader2,
  Wrench,
  Lightbulb,
  AlertTriangle,
  Check,
  PlusCircle,
  X,
  Trash2,
} from "lucide-react";
import {
  getAuthoring,
  startAuthoring,
  dismissAuthoringProposal,
  type AuthoringStep,
  type ProposedCheck,
  type SuggestedSample,
  type RemovedSample,
} from "@/api/authoring";
import {
  bulkAddVerificationSamples,
  deleteVerificationSample,
} from "@/api/verification";
import { ApiError } from "@/api/client";
import { useCheckAuthoringProgress } from "@/hooks/useEventStream";

/**
 * "Improve with AI" panel for the /checks/:id edit page.
 *
 * POSTs the editor's CURRENT working rules + brief; a GPT agentic loop on the
 * API process researches, validates against the verification corpus, and
 * proposes a revision. Progress streams live via `check_authoring_progress`
 * SSE; the terminal frame refetches GET /authoring for the proposed revision.
 * "Apply suggestion" loads the proposed rules back into the editor (not
 * saved) so the operator reviews + Saves through the normal check flow.
 *
 * Re-running re-seeds from whatever's currently in the editor — so accept a
 * suggestion, tweak, add samples, and improve again to build on it rather
 * than starting over.
 */
export function AiImprovePanel({
  checkId,
  brief,
  workingPositive,
  workingNegative,
  onApply,
}: {
  checkId: string;
  brief: string | null;
  workingPositive: Array<Record<string, unknown>>;
  workingNegative: Array<Record<string, unknown>>;
  onApply: (proposed: ProposedCheck) => void;
}) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ["authoring", checkId],
    queryFn: () => getAuthoring(checkId),
    refetchOnWindowFocus: false,
  });
  const progress = useCheckAuthoringProgress(checkId);

  // Accumulate the live step log per run; reset when a new run id appears.
  const [liveSteps, setLiveSteps] = useState<AuthoringStep[]>([]);
  const liveRunRef = useRef<string | null>(null);
  useEffect(() => {
    if (!progress) return;
    if (progress.run_id !== liveRunRef.current) {
      liveRunRef.current = progress.run_id;
      setLiveSteps([]);
    }
    if (progress.step) setLiveSteps((s) => [...s, progress.step as AuthoringStep]);
  }, [progress]);

  const latest = query.data?.latest_run ?? null;
  const running = progress ? !progress.done : latest?.status === "running";
  const steps = running ? liveSteps : latest?.steps ?? [];
  // A dismissed run keeps its proposal on the doc (audit) but the panel hides
  // it — so a discarded suggestion doesn't linger until the next improve run.
  const proposed =
    !running && !latest?.dismissed ? latest?.proposed_check ?? null : null;

  const start = useMutation({
    mutationFn: () =>
      startAuthoring(checkId, {
        brief,
        working_positive_rules: workingPositive,
        working_negative_rules: workingNegative,
      }),
    onSuccess: () => {
      setLiveSteps([]);
      qc.invalidateQueries({ queryKey: ["authoring", checkId] });
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 409) {
        toast.error("An improve run is already in progress for this check.");
      } else if (e instanceof ApiError && e.status === 422) {
        toast.error(typeof e.detail === "string" ? e.detail : "Corpus too large.");
      } else {
        toast.error("Could not start the improve run.");
      }
    },
  });

  const dismiss = useMutation({
    mutationFn: (runId: string) => dismissAuthoringProposal(checkId, runId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["authoring", checkId] }),
    onError: () => toast.error("Could not discard the proposal."),
  });

  const usage = latest?.token_usage;

  return (
    <section className="card overflow-hidden">
      <div className="px-4 py-2 border-b border-border flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Sparkles className="h-4 w-4 text-accent" /> Improve with AI
        </div>
        <button
          className="btn-primary text-xs"
          onClick={() => start.mutate()}
          disabled={running || start.isPending}
        >
          {running ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Improving…
            </>
          ) : (
            <>
              <Sparkles className="h-3.5 w-3.5" /> Improve
            </>
          )}
        </button>
      </div>

      <div className="p-4 space-y-3">
        <p className="text-xs text-muted leading-relaxed">
          Sends the current rules above + the brief + this check's verification
          samples to a GPT agent. It researches the sample sites, drafts revised
          rules, validates them against the corpus, and proposes a revision you
          can apply and save. Add positive/negative samples below first for the
          best result.
        </p>

        {latest?.status === "failed" && !running ? (
          <div className="flex items-start gap-2 rounded-md border border-danger/40 bg-danger/10 p-2 text-xs text-danger">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>Last run failed: {latest.error ?? "unknown error"}</span>
          </div>
        ) : null}

        {steps.length > 0 ? <StepLog steps={steps} running={!!running} /> : null}

        {proposed ? (
          <ProposedRevision
            proposed={proposed}
            checkId={checkId}
            onApply={() => onApply(proposed)}
            onDiscard={() => latest && dismiss.mutate(latest.id)}
            discarding={dismiss.isPending}
          />
        ) : null}

        {usage && usage.requests > 0 ? (
          <p className="text-[11px] text-muted tabular-nums">
            {usage.requests} model call{usage.requests === 1 ? "" : "s"} ·{" "}
            {usage.prompt_tokens + usage.completion_tokens} tokens
          </p>
        ) : null}
      </div>
    </section>
  );
}

function StepLog({ steps, running }: { steps: AuthoringStep[]; running: boolean }) {
  return (
    <ol className="space-y-1.5">
      {steps.map((s, i) => (
        <li key={i} className="flex items-start gap-2 text-xs">
          <StepIcon kind={s.kind} />
          <div className="min-w-0">
            <span className="font-medium">{s.summary}</span>
            {s.kind === "plan" && isDiagnostic(s) ? (
              <DiagnosticLine data={s.data} />
            ) : null}
          </div>
        </li>
      ))}
      {running ? (
        <li className="flex items-center gap-2 text-xs text-muted">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> working…
        </li>
      ) : null}
    </ol>
  );
}

function StepIcon({ kind }: { kind: AuthoringStep["kind"] }) {
  if (kind === "tool_call")
    return <Wrench className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted" />;
  if (kind === "proposal")
    return <Lightbulb className="h-3.5 w-3.5 mt-0.5 shrink-0 text-accent" />;
  if (kind === "error")
    return <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-danger" />;
  return <Check className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted" />;
}

function isDiagnostic(s: AuthoringStep): boolean {
  return "diagnostic" in (s.data ?? {});
}

function DiagnosticLine({ data }: { data: Record<string, unknown> }) {
  const diag = data.diagnostic as
    | { false_positives: number | null; false_negatives: number | null }
    | undefined;
  if (!diag) return null;
  return (
    <span className="ml-2 text-muted">
      current rules: {diag.false_negatives ?? "?"} missed positives,{" "}
      {diag.false_positives ?? "?"} false matches
    </span>
  );
}

function ProposedRevision({
  proposed,
  checkId,
  onApply,
  onDiscard,
  discarding,
}: {
  proposed: ProposedCheck;
  checkId: string;
  onApply: () => void;
  onDiscard: () => void;
  discarding: boolean;
}) {
  return (
    <div className="rounded-md border border-accent/40 bg-accent/5 p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs font-semibold text-accent">
          <Lightbulb className="h-4 w-4" /> Proposed revision
        </div>
        <div className="flex items-center gap-2">
          <button
            className="btn-ghost text-xs"
            onClick={onDiscard}
            disabled={discarding}
          >
            {discarding ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <X className="h-3.5 w-3.5" />
            )}{" "}
            Discard
          </button>
          <button className="btn-primary text-xs" onClick={onApply}>
            <Check className="h-3.5 w-3.5" /> Apply to editor
          </button>
        </div>
      </div>
      {proposed.rationale ? (
        <p className="text-xs text-muted leading-relaxed">{proposed.rationale}</p>
      ) : null}
      <div className="text-[11px] text-muted">
        +{proposed.positive_rules.length} positive / −
        {proposed.negative_rules.length} negative rules
        {proposed.llm_verification_prompt ? " · + LLM verification gate" : ""}
      </div>
      <details className="text-xs">
        <summary className="cursor-pointer text-muted">Show proposed rules</summary>
        <pre className="mt-1 p-2 rounded-md bg-subtle font-mono text-[11px] overflow-auto whitespace-pre-wrap break-words">
          {JSON.stringify(
            {
              positive_rules: proposed.positive_rules,
              negative_rules: proposed.negative_rules,
              ...(proposed.llm_verification_prompt
                ? { llm_verification_prompt: proposed.llm_verification_prompt }
                : {}),
            },
            null,
            2,
          )}
        </pre>
      </details>
      <p className="text-[11px] text-muted">
        Applying loads these into the editor — review, then Save to keep them.
      </p>
      {proposed.suggested_samples && proposed.suggested_samples.length > 0 ? (
        <SuggestedSamples
          checkId={checkId}
          samples={proposed.suggested_samples}
        />
      ) : null}
      {proposed.removed_samples && proposed.removed_samples.length > 0 ? (
        <RemovedSamples checkId={checkId} samples={proposed.removed_samples} />
      ) : null}
    </div>
  );
}

/**
 * Domains the agent discovered + judged that aren't in the corpus yet. The
 * operator one-clicks them in (with the agent's guessed labels) and re-runs
 * Improve — the discover → label → resume loop. Labels can be flipped in the
 * corpus panel afterwards.
 */
function SuggestedSamples({
  checkId,
  samples,
}: {
  checkId: string;
  samples: SuggestedSample[];
}) {
  const qc = useQueryClient();
  const add = useMutation({
    mutationFn: async () => {
      const pos = samples.filter((s) => s.expected === "positive").map((s) => s.url);
      const neg = samples.filter((s) => s.expected === "negative").map((s) => s.url);
      if (pos.length)
        await bulkAddVerificationSamples(checkId, { expected: "positive", urls: pos });
      if (neg.length)
        await bulkAddVerificationSamples(checkId, { expected: "negative", urls: neg });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["verification", checkId] });
      toast.success("Added to corpus — click Improve to re-run with them.");
    },
    onError: () => toast.error("Could not add the suggested samples."),
  });
  return (
    <div className="rounded-md border border-border bg-subtle/50 p-2 space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-semibold">
          {samples.length} suggested sample{samples.length === 1 ? "" : "s"} to label
        </span>
        <button
          className="btn-ghost text-[11px]"
          onClick={() => add.mutate()}
          disabled={add.isPending}
        >
          <PlusCircle className="h-3.5 w-3.5" /> Add all to corpus
        </button>
      </div>
      <ul className="space-y-1">
        {samples.map((s, i) => (
          <li key={i} className="flex items-start gap-2 text-[11px]">
            <span
              className={
                s.expected === "positive"
                  ? "shrink-0 rounded px-1 text-success bg-success/10"
                  : "shrink-0 rounded px-1 text-danger bg-danger/10"
              }
            >
              {s.expected === "positive" ? "pos" : "neg"}
            </span>
            <span className="min-w-0">
              <span className="font-mono break-all">{s.url}</span>
              {s.reason ? <span className="text-muted"> — {s.reason}</span> : null}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Existing corpus samples the agent flagged as bad data — unreachable, or
 * mislabeled / off-topic. The operator one-clicks them out of the corpus.
 * `allSettled` so a re-click (samples already gone → 404) doesn't abort the
 * batch; the count reports what actually succeeded.
 */
function RemovedSamples({
  checkId,
  samples,
}: {
  checkId: string;
  samples: RemovedSample[];
}) {
  const qc = useQueryClient();
  const remove = useMutation({
    mutationFn: async () => {
      const results = await Promise.allSettled(
        samples.map((s) => deleteVerificationSample(checkId, s.sample_id)),
      );
      return results.filter((r) => r.status === "fulfilled").length;
    },
    onSuccess: (removed) => {
      qc.invalidateQueries({ queryKey: ["verification", checkId] });
      toast.success(`Removed ${removed} from corpus.`);
    },
    onError: () => toast.error("Could not remove the samples."),
  });
  return (
    <div className="rounded-md border border-border bg-subtle/50 p-2 space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-semibold">
          {samples.length} sample{samples.length === 1 ? "" : "s"} to remove
        </span>
        <button
          className="btn-ghost text-[11px]"
          onClick={() => remove.mutate()}
          disabled={remove.isPending}
        >
          <Trash2 className="h-3.5 w-3.5" /> Remove all from corpus
        </button>
      </div>
      <ul className="space-y-1">
        {samples.map((s, i) => (
          <li key={i} className="flex items-start gap-2 text-[11px]">
            <span
              className={
                s.expected === "positive"
                  ? "shrink-0 rounded px-1 text-success bg-success/10"
                  : "shrink-0 rounded px-1 text-danger bg-danger/10"
              }
            >
              {s.expected === "positive" ? "pos" : "neg"}
            </span>
            <span className="min-w-0">
              <span className="font-mono break-all">{s.url}</span>
              {s.reason ? <span className="text-muted"> — {s.reason}</span> : null}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
